import axios from 'axios';

// Configuração base da API
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1'
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1');

// Instância do axios para Telão Brasil
const api = axios.create({
  baseURL: `${API_BASE_URL}/telao-brasil`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requests
api.interceptors.request.use(
  (config) => {
    console.log(`🔄 Telão Brasil API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor para responses
api.interceptors.response.use(
  (response) => {
    console.log(`✅ Telão Brasil API Response: ${response.status}`);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          error.userMessage = data.error || 'Dados inválidos enviados';
          break;
        case 403:
          error.userMessage = data.message || 'Enquete encerrada';
          break;
        case 409:
          error.userMessage = data.message || 'Você já votou nesta enquete';
          break;
        case 429:
          error.userMessage = 'Muitas tentativas. Aguarde alguns minutos.';
          break;
        case 500:
          error.userMessage = 'Erro interno do servidor. Tente novamente.';
          break;
        default:
          error.userMessage = data.error || 'Erro desconhecido';
      }
    } else if (error.request) {
      error.userMessage = 'Erro de conexão. Verifique sua internet.';
    } else {
      error.userMessage = 'Erro interno da aplicação';
    }
    
    return Promise.reject(error);
  }
);

// Serviços da API Telão Brasil
export const telaBrasilAPI = {
  // Gera token de rastreamento
  async gerarToken() {
    try {
      const response = await api.get('/token');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Registra voto
  async registrarVoto(dadosVoto) {
    try {
      const response = await api.post('/votar', dadosVoto);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtém status da enquete
  async obterStatus() {
    try {
      const response = await api.get('/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtém estatísticas públicas
  async obterEstatisticas() {
    try {
      const response = await api.get('/estatisticas');
      
      const stats = response.data.estatisticas;
      
      // Mapeia nomes dos locais
      const nomesLocais = {
        'praca_nova_corumba': '🏛️ Praça da Nova Corumbá',
        'porto_geral': '⚓ Porto Geral',
        'esplanada_nob': '🚂 Esplanada da NOB',
        'praca_cravo_vermelho': '🌹 Praça do Cravo Vermelho',
        'praca_generoso': '🌳 Bairro do Generoso',
        'praca_ceu': '⭐ Praça CEU'
      };
      
      // Formata dados para o gráfico
      return {
        total: stats.total,
        locais: stats.porLocal.map(item => ({
          local: item.local,
          nome: nomesLocais[item.local] || item.local,
          votos: item.votos,
          percentual: item.percentual
        }))
      };
    } catch (error) {
      throw error;
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Utilitários para localStorage (específico para Telão Brasil)
export const telaBrasilStorage = {
  // Salva token de rastreamento
  saveTrackingToken(token) {
    try {
      const data = {
        token,
        timestamp: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000)
      };
      localStorage.setItem('telao_brasil_tracking_token', JSON.stringify(data));
      console.log('💾 Token Telão Brasil salvo');
    } catch (error) {
      console.error('Erro ao salvar token:', error);
    }
  },

  // Obtém token de rastreamento válido
  getTrackingToken() {
    try {
      const stored = localStorage.getItem('telao_brasil_tracking_token');
      if (!stored) return null;

      const data = JSON.parse(stored);
      
      if (Date.now() > data.expiresAt) {
        this.clearTrackingToken();
        return null;
      }

      return data.token;
    } catch (error) {
      console.error('Erro ao recuperar token:', error);
      this.clearTrackingToken();
      return null;
    }
  },

  // Remove token de rastreamento
  clearTrackingToken() {
    try {
      localStorage.removeItem('telao_brasil_tracking_token');
      console.log('🗑️ Token Telão Brasil removido');
    } catch (error) {
      console.error('Erro ao remover token:', error);
    }
  },

  // Verifica se já votou
  hasVoted() {
    try {
      const voted = localStorage.getItem('telao_brasil_voted');
      return voted === 'true';
    } catch (error) {
      return false;
    }
  },

  // Marca como votado
  markAsVoted() {
    try {
      localStorage.setItem('telao_brasil_voted', 'true');
      localStorage.setItem('telao_brasil_voted_at', Date.now().toString());
      console.log('✅ Marcado como votado - Telão Brasil');
    } catch (error) {
      console.error('Erro ao marcar como votado:', error);
    }
  }
};

// Validação específica para Telão Brasil
export const telaBrasilValidation = {
  validateFormData(data) {
    const errors = [];

    // Valida local escolhido
    const locaisValidos = [
      'praca_nova_corumba',
      'porto_geral',
      'esplanada_nob',
      'praca_cravo_vermelho',
      'praca_generoso',
      'praca_ceu'
    ];

    if (!data.local_escolhido) {
      errors.push('Você deve escolher um local');
    } else if (!locaisValidos.includes(data.local_escolhido)) {
      errors.push('Local escolhido é inválido');
    }

    // Validação de tempo mínimo
    if (!data.tempo_preenchimento_ms || data.tempo_preenchimento_ms < 2000) {
      errors.push('Tempo de preenchimento muito rápido');
    }

    // Validação de token
    if (!data.token_rastreamento) {
      errors.push('Token de rastreamento é obrigatório');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default api;
